/**
 * The one "Remind me about this" sheet of the window (mounted by AppShell): message menus open it for a message, Later
 * rows to set or snooze an item's reminder. Picking saves through the workspace's LaterViewModel and confirms with a
 * toast ("Reminder set for …").
 */
import type { RemindPreset } from "@bundle/core";
import { Later } from "@bundle/core";
import { RemindSheet } from "../components/RemindSheet.tsx";
import { Toast } from "../primitives/index.ts";
import { useFormat, useT } from "../i18n/I18nProvider.tsx";
import { useWorkspace } from "../hooks/index.ts";
import { useState, useSyncExternalStore } from "react";
import { useLater } from "./registry.ts";

export type RemindTarget = { kind: "message"; messageId: string; channelId: string } | { kind: "item"; id: string };

let current: RemindTarget | undefined;
const listeners = new Set<() => void>();
const set = (t: RemindTarget | undefined) => {
  current = t;
  for (const l of listeners) l();
};

/** Opens the sheet for a message or a Later item. */
export function openRemind(target: RemindTarget): void {
  set(target);
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};

export function RemindHost() {
  const t = useT();
  const fmt = useFormat();
  const ws = useWorkspace();
  const later = useLater();
  const target = useSyncExternalStore(subscribe, () => current);
  const [done, setDone] = useState<number | undefined>(undefined);
  const now = ws.vm.deps.clock.now();
  const offset = ws.vm.deps.clock.tzOffsetMinutes(now);
  const pick = (when: RemindPreset | number) => {
    if (later === undefined || target === undefined) return;
    const at = typeof when === "number" ? when : Later.presetTime(when, ws.vm.deps.clock.now(), offset);
    if (target.kind === "message") void later.saveMessage(target.messageId, target.channelId, at);
    else later.remind(target.id, at);
    setDone(at);
  };
  return (
    <>
      <RemindSheet open={target !== undefined} onClose={() => set(undefined)} onPick={pick} now={now} offsetMinutes={offset} testID="remind-sheet" />
      <Toast
        open={done !== undefined}
        onClose={() => setDone(undefined)}
        message={done === undefined ? "" : t("remind.done", { when: fmt.timestamp(done) })}
        closeLabel={t("common.close")}
        durationMs={4000}
        testID="remind-done"
      />
    </>
  );
}
